import { useState, useEffect, useRef } from 'react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Switch } from '@/components/ui/switch';
import { toast } from 'sonner';
import axios from 'axios';
import { QRCodeCanvas } from 'qrcode.react';
import {
  Music, Camera, Palette, Trash2, Plus, Save, Download, Copy,
  QrCode, Eye, Loader2, X, Settings, Sparkles, Image as ImageIcon,
  Check, ExternalLink, Upload, Monitor, Info, Users, Mail, Send
} from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL;
const API_BASE = `${API}/api`;

const FONT_OPTIONS = [
  { value: 'elegant', label: 'Elegant (Serif)' },
  { value: 'modern', label: 'Modern (Sans)' },
  { value: 'fun', label: 'Fun (Mono)' },
];

const BG_PRESETS = [
  { label: 'Midnight', type: 'gradient', value: '#1a1a2e,#16213e' },
  { label: 'Rose', type: 'gradient', value: '#2d1b2e,#1a1a2e' },
  { label: 'Ocean', type: 'gradient', value: '#0a1628,#1a2a4a' },
  { label: 'Forest', type: 'gradient', value: '#0f1a0f,#1a2e1a' },
  { label: 'Sunset', type: 'gradient', value: '#2e1a1a,#1a1a2e' },
  { label: 'Dark', type: 'solid', value: '#0a0a0a' },
  { label: 'Navy', type: 'solid', value: '#0f172a' },
  { label: 'Charcoal', type: 'solid', value: '#1c1c1c' },
];

const COLOR_PRESETS = [
  '#e91e63', '#f44336', '#ff5722', '#ff9800', '#ffc107',
  '#4caf50', '#009688', '#00bcd4', '#2196f3', '#3f51b5',
  '#9c27b0', '#673ab7', '#795548', '#607d8b', '#ffffff',
];

export default function EventExperienceSection({ token, booking, portalUrl }) {
  const [experience, setExperience] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [activeSubTab, setActiveSubTab] = useState('design');
  const [requests, setRequests] = useState([]);
  const [photos, setPhotos] = useState([]);
  const [showQr, setShowQr] = useState(false);

  // Do-not-play form
  const [dnpTitle, setDnpTitle] = useState('');
  const [dnpArtist, setDnpArtist] = useState('');

  // Photo upload for couple photo
  const couplePhotoRef = useRef(null);
  const featuredPhotoRef = useRef(null);
  const [uploadingPhoto, setUploadingPhoto] = useState(false);
  const [uploadingFeatured, setUploadingFeatured] = useState(false);
  const [guests, setGuests] = useState([]);
  const [sendingEmails, setSendingEmails] = useState(false);
  const [downloadingZip, setDownloadingZip] = useState(false);

  useEffect(() => {
    fetchExperience();
  }, [token]);

  useEffect(() => {
    if (experience && activeSubTab === 'requests') fetchRequests();
    if (experience && activeSubTab === 'photos') { fetchPhotos(); fetchGuests(); }
    if (experience && activeSubTab === 'guests') fetchGuests();
  }, [activeSubTab, experience]);

  const fetchExperience = async () => {
    try {
      const res = await axios.get(`${API_BASE}/client-portal/${token}/event-experience`);
      if (res.data.exists === false) {
        setExperience(null);
      } else {
        setExperience(res.data);
      }
    } catch {
      setExperience(null);
    } finally {
      setLoading(false);
    }
  };

  const createExperience = async () => {
    try {
      const res = await axios.post(`${API_BASE}/client-portal/${token}/event-experience`);
      setExperience(res.data);
      toast.success('Guest Experience created!');
    } catch (err) {
      toast.error('Failed to create experience');
    }
  };

  const saveExperience = async (updates) => {
    setSaving(true);
    try {
      const res = await axios.put(`${API_BASE}/client-portal/${token}/event-experience`, updates);
      setExperience(res.data);
      toast.success('Saved!');
    } catch {
      toast.error('Failed to save');
    } finally {
      setSaving(false);
    }
  };

  const updateField = (field, value) => {
    setExperience(prev => ({ ...prev, [field]: value }));
  };

  const fetchRequests = async () => {
    try {
      const res = await axios.get(`${API_BASE}/client-portal/${token}/event-experience/requests`);
      setRequests(res.data);
    } catch { /* silent */ }
  };

  const fetchPhotos = async () => {
    try {
      const res = await axios.get(`${API_BASE}/client-portal/${token}/event-experience/photos`);
      setPhotos(res.data);
    } catch { /* silent */ }
  };

  const fetchGuests = async () => {
    try {
      const res = await axios.get(`${API_BASE}/client-portal/${token}/event-experience/guests`);
      setGuests(res.data);
    } catch { /* silent */ }
  };

  const deleteGuest = async (guestId, guestName) => {
    if (!window.confirm(`Remove ${guestName} from the guest list?`)) return;
    try {
      await axios.delete(`${API_BASE}/client-portal/${token}/event-experience/guests/${guestId}`);
      setGuests(prev => prev.filter(g => g.id !== guestId));
      toast.success(`${guestName} removed`);
    } catch {
      toast.error('Failed to remove guest');
    }
  };

  const downloadAllPhotos = async () => {
    setDownloadingZip(true);
    try {
      const res = await axios.get(`${API_BASE}/client-portal/${token}/event-experience/photos/download`, {
        responseType: 'blob'
      });
      const url = window.URL.createObjectURL(new Blob([res.data]));
      const a = document.createElement('a');
      a.href = url;
      a.download = `${(experience.event_title || 'event').replace(/[^a-zA-Z0-9 _-]/g, '')}_photos.zip`;
      a.click();
      window.URL.revokeObjectURL(url);
      toast.success('Photos downloaded!');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to download');
    } finally {
      setDownloadingZip(false);
    }
  };

  const sendGalleryEmails = async () => {
    setSendingEmails(true);
    try {
      const res = await axios.post(`${API_BASE}/client-portal/${token}/event-experience/send-gallery-email`);
      toast.success(`Gallery link sent to ${res.data.sent} guest${res.data.sent !== 1 ? 's' : ''}!`);
      if (res.data.failed > 0) toast.error(`${res.data.failed} email${res.data.failed !== 1 ? 's' : ''} failed to send`);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to send emails');
    } finally {
      setSendingEmails(false);
    }
  };

  const removeRequest = async (requestId) => {
    try {
      await axios.delete(`${API_BASE}/client-portal/${token}/event-experience/requests/${requestId}`);
      setRequests(prev => prev.filter(r => r.id !== requestId));
      toast.success('Request removed');
    } catch {
      toast.error('Failed to remove');
    }
  };

  const addDoNotPlay = () => {
    if (!dnpTitle.trim() || !dnpArtist.trim()) { toast.error('Enter song title and artist'); return; }
    const newDnp = [...(experience.do_not_play || []), { title: dnpTitle.trim(), artist: dnpArtist.trim() }];
    setExperience(prev => ({ ...prev, do_not_play: newDnp }));
    saveExperience({ do_not_play: newDnp });
    setDnpTitle(''); setDnpArtist('');
  };

  const removeDoNotPlay = (index) => {
    const newDnp = (experience.do_not_play || []).filter((_, i) => i !== index);
    setExperience(prev => ({ ...prev, do_not_play: newDnp }));
    saveExperience({ do_not_play: newDnp });
  };

  const handleCouplePhotoUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploadingPhoto(true);
    try {
      const formData = new FormData();
      formData.append('file', file);
      formData.append('folder', 'event-branding');
      const djToken = localStorage.getItem('token');
      const headers = djToken ? { Authorization: `Bearer ${djToken}` } : {};
      const res = await axios.post(`${API_BASE}/upload`, formData, { headers });
      const photoUrl = res.data.url;
      updateField('couple_photo_url', photoUrl);
      await saveExperience({ couple_photo_url: photoUrl });
      toast.success('Photo uploaded!');
    } catch {
      toast.error('Failed to upload photo');
    } finally {
      setUploadingPhoto(false);
      if (couplePhotoRef.current) couplePhotoRef.current.value = '';
    }
  };

  const handleFeaturedPhotoUpload = async (e) => {
    const files = Array.from(e.target.files || []);
    if (!files.length) return;
    setUploadingFeatured(true);
    const newFeatured = [...(experience.featured_photos || [])];
    for (const file of files) {
      try {
        const formData = new FormData();
        formData.append('file', file);
        formData.append('folder', 'event-featured');
        const djToken = localStorage.getItem('token');
        const headers = djToken ? { Authorization: `Bearer ${djToken}` } : {};
        const res = await axios.post(`${API_BASE}/upload`, formData, { headers });
        newFeatured.push({ photo_url: res.data.url, caption: '' });
      } catch {
        toast.error(`Failed to upload ${file.name}`);
      }
    }
    updateField('featured_photos', newFeatured);
    await saveExperience({ featured_photos: newFeatured });
    toast.success(`${files.length} photo${files.length > 1 ? 's' : ''} added to slideshow!`);
    setUploadingFeatured(false);
    if (featuredPhotoRef.current) featuredPhotoRef.current.value = '';
  };

  const removeFeaturedPhoto = (index) => {
    const newFeatured = (experience.featured_photos || []).filter((_, i) => i !== index);
    updateField('featured_photos', newFeatured);
    saveExperience({ featured_photos: newFeatured });
  };

  const eventUrl = experience ? `${window.location.origin}/event/${experience.event_code}` : '';
  const slideshowUrl = experience ? `${window.location.origin}/event/${experience.event_code}/slideshow` : '';
  const galleryUrl = experience ? `${window.location.origin}/event/${experience.event_code}/gallery` : '';

  const copyLink = () => {
    navigator.clipboard.writeText(eventUrl);
    toast.success('Link copied!');
  };

  const downloadQr = () => {
    const canvas = document.querySelector('#event-qr-canvas canvas');
    if (!canvas) return;
    const url = canvas.toDataURL('image/png');
    const a = document.createElement('a');
    a.href = url;
    a.download = `event-qr-${experience.event_code}.png`;
    a.click();
  };

  if (loading) return (
    <div className="flex items-center justify-center py-20">
      <Loader2 className="w-8 h-8 animate-spin text-white/30" />
    </div>
  );

  // Setup screen
  if (!experience) return (
    <div className="text-center py-16 space-y-6" data-testid="event-experience-setup">
      <div className="w-20 h-20 mx-auto rounded-2xl bg-gradient-to-br from-pink-500/20 to-purple-500/20 flex items-center justify-center">
        <Sparkles className="w-10 h-10 text-pink-400" />
      </div>
      <div className="max-w-lg mx-auto">
        <h2 className="text-2xl font-bold text-white">Guest Experience</h2>
        <p className="text-white/50 mt-3 text-base leading-relaxed">
          Give your guests a way to be part of the event! A simple QR code lets everyone request songs, 
          dedicate tracks to someone special, and share photos from the night — all from their phone.
        </p>
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 mt-6 text-left">
          <div className="bg-white/5 rounded-xl p-3 border border-white/10">
            <Music className="w-5 h-5 text-pink-400 mb-2" />
            <p className="text-white text-sm font-medium">Song Requests</p>
            <p className="text-white/40 text-xs mt-1">Guests search your DJ's library and request songs live</p>
          </div>
          <div className="bg-white/5 rounded-xl p-3 border border-white/10">
            <Camera className="w-5 h-5 text-pink-400 mb-2" />
            <p className="text-white text-sm font-medium">Photo Gallery</p>
            <p className="text-white/40 text-xs mt-1">Guests snap and share photos you can download later</p>
          </div>
          <div className="bg-white/5 rounded-xl p-3 border border-white/10">
            <Monitor className="w-5 h-5 text-pink-400 mb-2" />
            <p className="text-white text-sm font-medium">Live Slideshow</p>
            <p className="text-white/40 text-xs mt-1">Display guest photos on event screens in real time</p>
          </div>
        </div>
      </div>
      <Button onClick={createExperience} className="bg-pink-500 hover:bg-pink-600 text-white rounded-xl px-8 h-12 text-base font-semibold" data-testid="create-experience-btn">
        <Sparkles className="w-5 h-5 mr-2" />Set Up Guest Experience
      </Button>
    </div>
  );

  const allSubTabs = [
    { id: 'design', label: 'Design', icon: Palette },
    { id: 'settings', label: 'Settings', icon: Settings },
    experience?.feature_slideshow !== false && { id: 'slideshow', label: 'Slideshow', icon: Monitor },
    experience?.feature_song_requests !== false && { id: 'requests', label: 'Requests', icon: Music },
    experience?.feature_photo_uploads !== false && { id: 'photos', label: 'Photos', icon: Camera },
    { id: 'guests', label: 'Guests', icon: Users },
  ].filter(Boolean);
  const subTabs = allSubTabs;

  return (
    <div className="space-y-4" data-testid="event-experience-section">
      {/* Info Blurb */}
      <div className="bg-blue-500/10 border border-blue-500/20 rounded-2xl p-4 flex gap-3" data-testid="experience-info-blurb">
        <Info className="w-5 h-5 text-blue-400 shrink-0 mt-0.5" />
        <div className="text-sm">
          <p className="text-white/70 leading-relaxed">
            Your guests scan the QR code below to request songs and share photos — right from their phone. 
            Customize the look, manage a do-not-play list, and review everything here. 
            You can also display a <span className="text-blue-400">live photo slideshow</span> on event screens.
          </p>
        </div>
      </div>

      {/* QR Code + Link Bar */}
      <div className="bg-white/5 rounded-2xl border border-white/10 p-4">
        <div className="flex flex-col sm:flex-row items-start sm:items-center gap-4">
          <div className="flex-1 min-w-0">
            <p className="text-white/40 text-xs uppercase tracking-wider mb-1">Guest Link</p>
            <p className="text-white text-sm font-mono truncate">{eventUrl}</p>
          </div>
          <div className="flex items-center gap-2 shrink-0">
            <Button variant="outline" size="sm" onClick={copyLink} className="border-white/20 text-white bg-white/5 hover:bg-white/10 rounded-xl" data-testid="copy-link-btn">
              <Copy className="w-4 h-4 mr-1.5" />Copy
            </Button>
            <Button variant="outline" size="sm" onClick={() => setShowQr(!showQr)} className="border-white/20 text-white bg-white/5 hover:bg-white/10 rounded-xl" data-testid="show-qr-btn">
              <QrCode className="w-4 h-4 mr-1.5" />{showQr ? 'Hide' : 'Show'} QR
            </Button>
            <a href={eventUrl} target="_blank" rel="noopener noreferrer">
              <Button variant="outline" size="sm" className="border-white/20 text-white bg-white/5 hover:bg-white/10 rounded-xl" data-testid="preview-link-btn">
                <Eye className="w-4 h-4 mr-1.5" />Preview
              </Button>
            </a>
          </div>
        </div>
        {showQr && (
          <div className="mt-4 flex flex-col items-center gap-3 py-4 bg-white rounded-xl" id="event-qr-canvas" data-testid="qr-code-display">
            <QRCodeCanvas value={eventUrl} size={200} level="H" includeMargin />
            <p className="text-gray-600 text-xs font-mono">{experience.event_code}</p>
            <Button onClick={downloadQr} variant="outline" size="sm" className="text-gray-600 border-gray-300">
              <Download className="w-4 h-4 mr-1.5" />Download QR Code
            </Button>
          </div>
        )}
      </div>

      {/* Sub-Tab Navigation */}
      <div className="flex gap-1 bg-white/5 p-1 rounded-xl">
        {subTabs.map(tab => (
          <button
            key={tab.id}
            onClick={() => setActiveSubTab(tab.id)}
            className={`flex-1 flex items-center justify-center gap-1.5 py-2 px-3 rounded-lg text-sm font-medium transition-all ${
              activeSubTab === tab.id ? 'bg-white/15 text-white' : 'text-white/40 hover:text-white/60'
            }`}
            data-testid={`subtab-${tab.id}`}
          >
            <tab.icon className="w-4 h-4" /><span className="hidden sm:inline">{tab.label}</span>
          </button>
        ))}
      </div>

      {/* ── Design Tab ── */}
      {activeSubTab === 'design' && (
        <div className="space-y-4" data-testid="design-tab">
          {/* Event Title & Message */}
          <div className="bg-white/5 rounded-2xl border border-white/10 p-4 space-y-4">
            <h3 className="text-white font-semibold text-sm">Event Details</h3>
            <div>
              <Label className="text-white/60 text-xs">Event Title</Label>
              <Input
                value={experience.event_title || ''}
                onChange={e => updateField('event_title', e.target.value)}
                className="mt-1 bg-white/10 border-white/15 text-white rounded-xl"
                data-testid="event-title-input"
              />
            </div>
            <div>
              <Label className="text-white/60 text-xs">Welcome Message</Label>
              <Input
                value={experience.welcome_message || ''}
                onChange={e => updateField('welcome_message', e.target.value)}
                className="mt-1 bg-white/10 border-white/15 text-white rounded-xl"
                data-testid="welcome-message-input"
              />
            </div>
          </div>

          {/* Couple Photo */}
          <div className="bg-white/5 rounded-2xl border border-white/10 p-4 space-y-3">
            <h3 className="text-white font-semibold text-sm">Couple/Event Photo</h3>
            <div className="flex items-center gap-4">
              {experience.couple_photo_url ? (
                <div className="relative">
                  <div className="w-20 h-20 rounded-full overflow-hidden border-2" style={{ borderColor: experience.primary_color }}>
                    <img
                      src={experience.couple_photo_url.startsWith('/') ? `${API}${experience.couple_photo_url}` : experience.couple_photo_url}
                      alt="Couple"
                      className="w-full h-full object-cover"
                    />
                  </div>
                  <button
                    onClick={() => { updateField('couple_photo_url', ''); saveExperience({ couple_photo_url: '' }); }}
                    className="absolute -top-1 -right-1 p-1 bg-red-500 rounded-full"
                  >
                    <X className="w-3 h-3 text-white" />
                  </button>
                </div>
              ) : (
                <div className="w-20 h-20 rounded-full bg-white/10 border-2 border-dashed border-white/20 flex items-center justify-center">
                  <ImageIcon className="w-8 h-8 text-white/20" />
                </div>
              )}
              <div>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => couplePhotoRef.current?.click()}
                  disabled={uploadingPhoto}
                  className="border-white/20 text-white bg-white/5 hover:bg-white/10 rounded-xl"
                  data-testid="upload-couple-photo-btn"
                >
                  {uploadingPhoto ? <Loader2 className="w-4 h-4 animate-spin mr-1.5" /> : <Upload className="w-4 h-4 mr-1.5" />}
                  {experience.couple_photo_url ? 'Change Photo' : 'Upload Photo'}
                </Button>
                <p className="text-white/30 text-xs mt-1">Displayed at the top of the guest page</p>
              </div>
              <input ref={couplePhotoRef} type="file" accept="image/*" onChange={handleCouplePhotoUpload} className="hidden" />
            </div>
          </div>

          {/* Colors */}
          <div className="bg-white/5 rounded-2xl border border-white/10 p-4 space-y-4">
            <h3 className="text-white font-semibold text-sm">Colors</h3>
            <div>
              <Label className="text-white/60 text-xs">Primary Color</Label>
              <div className="flex flex-wrap gap-2 mt-2">
                {COLOR_PRESETS.map(c => (
                  <button
                    key={c}
                    onClick={() => updateField('primary_color', c)}
                    className={`w-8 h-8 rounded-full border-2 transition-transform ${experience.primary_color === c ? 'scale-125 border-white' : 'border-transparent hover:scale-110'}`}
                    style={{ background: c }}
                    data-testid={`primary-color-${c.replace('#', '')}`}
                  />
                ))}
              </div>
            </div>
            <div>
              <Label className="text-white/60 text-xs">Accent Color</Label>
              <div className="flex flex-wrap gap-2 mt-2">
                {COLOR_PRESETS.map(c => (
                  <button
                    key={c}
                    onClick={() => updateField('accent_color', c)}
                    className={`w-8 h-8 rounded-full border-2 transition-transform ${experience.accent_color === c ? 'scale-125 border-white' : 'border-transparent hover:scale-110'}`}
                    style={{ background: c }}
                    data-testid={`accent-color-${c.replace('#', '')}`}
                  />
                ))}
              </div>
            </div>
          </div>

          {/* Background */}
          <div className="bg-white/5 rounded-2xl border border-white/10 p-4 space-y-3">
            <h3 className="text-white font-semibold text-sm">Background</h3>
            <div className="grid grid-cols-4 gap-2">
              {BG_PRESETS.map(bg => {
                const isActive = experience.background_type === bg.type && experience.background_value === bg.value;
                const style = bg.type === 'gradient'
                  ? { background: `linear-gradient(135deg, ${bg.value.split(',')[0]}, ${bg.value.split(',')[1]})` }
                  : { background: bg.value };
                return (
                  <button
                    key={bg.label}
                    onClick={() => { updateField('background_type', bg.type); updateField('background_value', bg.value); }}
                    className={`h-16 rounded-xl border-2 transition-all ${isActive ? 'border-white scale-105' : 'border-white/10 hover:border-white/30'}`}
                    style={style}
                    data-testid={`bg-preset-${bg.label.toLowerCase()}`}
                  >
                    <span className="text-white/70 text-xs">{bg.label}</span>
                  </button>
                );
              })}
            </div>
          </div>

          {/* Font */}
          <div className="bg-white/5 rounded-2xl border border-white/10 p-4 space-y-3">
            <h3 className="text-white font-semibold text-sm">Font Style</h3>
            <div className="flex gap-2">
              {FONT_OPTIONS.map(f => (
                <button
                  key={f.value}
                  onClick={() => updateField('font_style', f.value)}
                  className={`flex-1 py-3 rounded-xl border transition-all text-sm ${
                    experience.font_style === f.value
                      ? 'border-white bg-white/15 text-white'
                      : 'border-white/10 text-white/40 hover:text-white/60'
                  }`}
                  data-testid={`font-${f.value}`}
                >
                  {f.label}
                </button>
              ))}
            </div>
          </div>

          {/* Save */}
          <Button
            onClick={() => saveExperience({
              event_title: experience.event_title,
              welcome_message: experience.welcome_message,
              primary_color: experience.primary_color,
              accent_color: experience.accent_color,
              font_style: experience.font_style,
              background_type: experience.background_type,
              background_value: experience.background_value,
            })}
            disabled={saving}
            className="w-full h-12 rounded-xl font-semibold"
            style={{ background: experience.primary_color || '#e91e63' }}
            data-testid="save-design-btn"
          >
            {saving ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Save className="w-4 h-4 mr-2" />}
            Save Design
          </Button>
        </div>
      )}

      {/* ── Settings Tab ── */}
      {activeSubTab === 'settings' && (
        <div className="space-y-4" data-testid="settings-tab">
          {/* Toggles */}
          <div className="bg-white/5 rounded-2xl border border-white/10 p-4 space-y-4">
            <h3 className="text-white font-semibold text-sm">Features</h3>
            {experience?.feature_song_requests !== false ? (
            <div className="flex items-center justify-between">
              <div>
                <p className="text-white text-sm">Song Requests</p>
                <p className="text-white/40 text-xs">Let guests request songs</p>
              </div>
              <Switch
                checked={experience.requests_enabled}
                onCheckedChange={v => { updateField('requests_enabled', v); saveExperience({ requests_enabled: v }); }}
                data-testid="toggle-requests"
              />
            </div>
            ) : (
            <div className="flex items-center justify-between opacity-40">
              <div>
                <p className="text-white text-sm">Song Requests</p>
                <p className="text-white/40 text-xs">Disabled by company settings</p>
              </div>
              <Switch checked={false} disabled />
            </div>
            )}
            {experience?.feature_photo_uploads !== false ? (
            <div className="flex items-center justify-between">
              <div>
                <p className="text-white text-sm">Photo Gallery</p>
                <p className="text-white/40 text-xs">Let guests upload photos</p>
              </div>
              <Switch
                checked={experience.photos_enabled}
                onCheckedChange={v => { updateField('photos_enabled', v); saveExperience({ photos_enabled: v }); }}
                data-testid="toggle-photos"
              />
            </div>
            ) : (
            <div className="flex items-center justify-between opacity-40">
              <div>
                <p className="text-white text-sm">Photo Gallery</p>
                <p className="text-white/40 text-xs">Disabled by company settings</p>
              </div>
              <Switch checked={false} disabled />
            </div>
            )}
            <div className="flex items-center justify-between">
              <div>
                <p className="text-white text-sm">Announcements / Dedications</p>
                <p className="text-white/40 text-xs">Let guests add notes like "Happy Birthday!"</p>
              </div>
              <Switch
                checked={experience.allow_announcements}
                onCheckedChange={v => { updateField('allow_announcements', v); saveExperience({ allow_announcements: v }); }}
                data-testid="toggle-announcements"
              />
            </div>
          </div>

          {/* Do Not Play List */}
          <div className="bg-white/5 rounded-2xl border border-white/10 p-4 space-y-3">
            <h3 className="text-white font-semibold text-sm">Do Not Play List</h3>
            <p className="text-white/40 text-xs">Songs here will be politely declined if a guest tries to request them.</p>
            <div className="flex gap-2">
              <Input
                placeholder="Song title"
                value={dnpTitle}
                onChange={e => setDnpTitle(e.target.value)}
                className="bg-white/10 border-white/15 text-white placeholder:text-white/30 rounded-xl"
                data-testid="dnp-title-input"
              />
              <Input
                placeholder="Artist"
                value={dnpArtist}
                onChange={e => setDnpArtist(e.target.value)}
                className="bg-white/10 border-white/15 text-white placeholder:text-white/30 rounded-xl"
                data-testid="dnp-artist-input"
              />
              <Button onClick={addDoNotPlay} size="sm" className="bg-red-500/20 hover:bg-red-500/30 text-red-400 rounded-xl shrink-0" data-testid="add-dnp-btn">
                <Plus className="w-4 h-4" />
              </Button>
            </div>
            {(experience.do_not_play || []).length > 0 && (
              <div className="space-y-1">
                {experience.do_not_play.map((song, i) => (
                  <div key={i} className="flex items-center justify-between bg-red-500/10 rounded-xl px-3 py-2">
                    <span className="text-white/70 text-sm">{song.title} &mdash; {song.artist}</span>
                    <button onClick={() => removeDoNotPlay(i)} className="text-red-400 hover:text-red-300 p-1" data-testid={`remove-dnp-${i}`}>
                      <X className="w-4 h-4" />
                    </button>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      )}

      {/* ── Requests Tab ── */}
      {activeSubTab === 'requests' && (
        <div className="space-y-3" data-testid="requests-tab">
          <div className="flex items-center justify-between">
            <h3 className="text-white font-semibold text-sm">{requests.length} Requests</h3>
            <Button variant="ghost" size="sm" onClick={fetchRequests} className="text-white/40 hover:text-white">
              <Loader2 className="w-4 h-4 mr-1" />Refresh
            </Button>
          </div>
          {requests.length === 0 ? (
            <div className="text-center py-12 bg-white/5 rounded-2xl">
              <Music className="w-12 h-12 text-white/10 mx-auto mb-3" />
              <p className="text-white/30 text-sm">No song requests yet</p>
            </div>
          ) : (
            <div className="space-y-2">
              {requests.map(r => (
                <div key={r.id} className={`bg-white/5 rounded-xl p-3 flex items-center gap-3 ${r.status === 'played' ? 'opacity-50' : ''}`}>
                  <div className="flex-1 min-w-0">
                    <p className="text-white text-sm font-medium truncate">{r.song_title} &mdash; {r.artist}</p>
                    <p className="text-white/40 text-xs">{r.guest_name} {r.note && `&middot; "${r.note}"`}</p>
                  </div>
                  {r.status === 'played' && <Check className="w-4 h-4 text-green-400 shrink-0" />}
                  <button onClick={() => removeRequest(r.id)} className="p-1.5 rounded-lg hover:bg-red-500/20 transition-colors" data-testid={`remove-req-${r.id}`}>
                    <Trash2 className="w-4 h-4 text-red-400/60" />
                  </button>
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {/* ── Slideshow Tab ── */}
      {activeSubTab === 'slideshow' && (
        <div className="space-y-4" data-testid="slideshow-tab">
          {/* Slideshow Link */}
          <div className="bg-white/5 rounded-2xl border border-white/10 p-4 space-y-3">
            <h3 className="text-white font-semibold text-sm flex items-center gap-2"><Monitor className="w-4 h-4 text-purple-400" />Slideshow Display</h3>
            <p className="text-white/40 text-xs">Open this link on a laptop connected to a screen at your event. It will automatically cycle through photos as guests upload them.</p>
            <div className="flex items-center gap-2">
              <Input value={slideshowUrl} readOnly className="bg-white/10 border-white/15 text-white text-sm font-mono rounded-xl" data-testid="slideshow-url" />
              <Button
                variant="outline"
                size="sm"
                onClick={() => { navigator.clipboard.writeText(slideshowUrl); toast.success('Slideshow link copied!'); }}
                className="border-white/20 text-white bg-white/5 hover:bg-white/10 rounded-xl shrink-0"
                data-testid="copy-slideshow-link-btn"
              >
                <Copy className="w-4 h-4 mr-1.5" />Copy
              </Button>
              <a href={slideshowUrl} target="_blank" rel="noopener noreferrer">
                <Button variant="outline" size="sm" className="border-white/20 text-white bg-white/5 hover:bg-white/10 rounded-xl shrink-0" data-testid="open-slideshow-btn">
                  <ExternalLink className="w-4 h-4 mr-1.5" />Open
                </Button>
              </a>
            </div>
          </div>

          {/* Fade Duration */}
          <div className="bg-white/5 rounded-2xl border border-white/10 p-4 space-y-3">
            <h3 className="text-white font-semibold text-sm">Slide Duration</h3>
            <p className="text-white/40 text-xs">How many seconds each photo stays on screen before fading to the next one.</p>
            <div className="flex items-center gap-3">
              {[3, 5, 6, 8, 10, 15].map(s => (
                <button
                  key={s}
                  onClick={() => { updateField('slideshow_fade_seconds', s); saveExperience({ slideshow_fade_seconds: s }); }}
                  className={`px-4 py-2 rounded-xl text-sm font-medium border transition-all ${
                    (experience.slideshow_fade_seconds || 6) === s
                      ? 'border-white bg-white/15 text-white'
                      : 'border-white/10 text-white/40 hover:text-white/60'
                  }`}
                  data-testid={`fade-${s}s`}
                >
                  {s}s
                </button>
              ))}
            </div>
          </div>

          {/* Featured/Starter Photos */}
          <div className="bg-white/5 rounded-2xl border border-white/10 p-4 space-y-3">
            <h3 className="text-white font-semibold text-sm">Featured Photos</h3>
            <p className="text-white/40 text-xs">Upload photos in advance so there's something on screen right away, even before guests start sharing. These appear first in the slideshow.</p>
            <Button
              variant="outline"
              size="sm"
              onClick={() => featuredPhotoRef.current?.click()}
              disabled={uploadingFeatured}
              className="border-white/20 text-white bg-white/5 hover:bg-white/10 rounded-xl"
              data-testid="upload-featured-btn"
            >
              {uploadingFeatured ? <Loader2 className="w-4 h-4 animate-spin mr-1.5" /> : <Upload className="w-4 h-4 mr-1.5" />}
              Add Photos
            </Button>
            <input ref={featuredPhotoRef} type="file" accept="image/*" multiple onChange={handleFeaturedPhotoUpload} className="hidden" />
            {(experience.featured_photos || []).length > 0 && (
              <div className="grid grid-cols-4 gap-2 mt-2">
                {experience.featured_photos.map((fp, i) => (
                  <div key={i} className="aspect-square rounded-xl overflow-hidden bg-white/5 relative group border border-white/10">
                    <img
                      src={fp.photo_url.startsWith('/') ? `${API}${fp.photo_url}` : fp.photo_url}
                      alt="" className="w-full h-full object-cover"
                    />
                    <button
                      onClick={() => removeFeaturedPhoto(i)}
                      className="absolute top-1 right-1 p-1 bg-red-500/80 rounded-full opacity-0 group-hover:opacity-100 transition-opacity"
                      data-testid={`remove-featured-${i}`}
                    >
                      <X className="w-3 h-3 text-white" />
                    </button>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      )}

      {/* ── Photos Tab ── */}
      {activeSubTab === 'photos' && (
        <div className="space-y-3" data-testid="photos-tab">
          <div className="flex items-center justify-between flex-wrap gap-2">
            <h3 className="text-white font-semibold text-sm">{photos.length} Photos</h3>
            <div className="flex items-center gap-2">
              {photos.length > 0 && (
                <>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={downloadAllPhotos}
                    disabled={downloadingZip}
                    className="border-white/20 text-white bg-white/5 hover:bg-white/10 rounded-xl"
                    data-testid="download-all-photos-btn"
                  >
                    {downloadingZip ? <Loader2 className="w-4 h-4 animate-spin mr-1.5" /> : <Download className="w-4 h-4 mr-1.5" />}
                    Download All
                  </Button>
                  <a href={galleryUrl} target="_blank" rel="noopener noreferrer">
                    <Button variant="outline" size="sm" className="border-white/20 text-white bg-white/5 hover:bg-white/10 rounded-xl" data-testid="open-gallery-btn">
                      <ExternalLink className="w-4 h-4 mr-1.5" />Gallery
                    </Button>
                  </a>
                </>
              )}
              <Button variant="ghost" size="sm" onClick={fetchPhotos} className="text-white/40 hover:text-white">
                <Loader2 className="w-4 h-4 mr-1" />Refresh
              </Button>
            </div>
          </div>
          {photos.length === 0 ? (
            <div className="text-center py-12 bg-white/5 rounded-2xl">
              <Camera className="w-12 h-12 text-white/10 mx-auto mb-3" />
              <p className="text-white/30 text-sm">No photos uploaded yet</p>
            </div>
          ) : (
            <div className="grid grid-cols-3 gap-2">
              {photos.map(photo => (
                <div key={photo.id} className="aspect-square rounded-xl overflow-hidden bg-white/5 relative group">
                  <img
                    src={photo.photo_url.startsWith('/') ? `${API}${photo.photo_url}` : photo.photo_url}
                    alt={photo.caption || ''}
                    className="w-full h-full object-cover"
                    loading="lazy"
                  />
                  <div className="absolute bottom-0 left-0 right-0 bg-black/60 px-2 py-1 opacity-0 group-hover:opacity-100 transition-opacity">
                    <p className="text-white text-xs truncate">{photo.guest_name}</p>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {/* ── Guests Tab ── */}
      {activeSubTab === 'guests' && (
        <div className="space-y-4" data-testid="guests-tab">
          <div className="flex items-center justify-between flex-wrap gap-2">
            <h3 className="text-white font-semibold text-sm">{guests.length} Guests Joined</h3>
            <div className="flex items-center gap-2">
              {guests.filter(g => g.guest_email).length > 0 && photos.length > 0 && (
                <Button
                  onClick={sendGalleryEmails}
                  disabled={sendingEmails}
                  size="sm"
                  className="rounded-xl font-medium"
                  style={{ background: experience?.primary_color || '#e91e63' }}
                  data-testid="send-gallery-emails-btn"
                >
                  {sendingEmails ? <Loader2 className="w-4 h-4 animate-spin mr-1.5" /> : <Send className="w-4 h-4 mr-1.5" />}
                  Send Gallery to Guests
                </Button>
              )}
              <Button variant="ghost" size="sm" onClick={fetchGuests} className="text-white/40 hover:text-white">
                <Loader2 className="w-4 h-4 mr-1" />Refresh
              </Button>
            </div>
          </div>

          <div className="bg-white/5 rounded-2xl border border-white/10 p-4">
            <div className="flex items-center gap-3 mb-3 pb-3 border-b border-white/10">
              <p className="text-white/40 text-xs flex-1">
                <Mail className="w-3.5 h-3.5 inline mr-1" />
                {guests.filter(g => g.guest_email).length} of {guests.length} guests provided their email
              </p>
              {galleryUrl && (
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => { navigator.clipboard.writeText(galleryUrl); toast.success('Gallery link copied!'); }}
                  className="border-white/20 text-white bg-white/5 hover:bg-white/10 rounded-xl text-xs"
                  data-testid="copy-gallery-link-btn"
                >
                  <Copy className="w-3.5 h-3.5 mr-1" />Copy Gallery Link
                </Button>
              )}
            </div>
            {guests.length === 0 ? (
              <div className="text-center py-8">
                <Users className="w-10 h-10 text-white/10 mx-auto mb-2" />
                <p className="text-white/30 text-sm">No guests have joined yet</p>
              </div>
            ) : (
              <div className="space-y-1.5">
                {guests.map(g => (
                  <div key={g.id} className="flex items-center gap-3 py-2 px-3 bg-white/[0.03] rounded-lg group">
                    <div className="w-8 h-8 rounded-full bg-white/10 flex items-center justify-center shrink-0">
                      <span className="text-white/50 text-sm font-medium">{(g.guest_name || '?')[0].toUpperCase()}</span>
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="text-white text-sm font-medium">{g.guest_name}</p>
                      {g.guest_email ? (
                        <p className="text-white/40 text-xs truncate">{g.guest_email}</p>
                      ) : (
                        <p className="text-white/20 text-xs italic">No email provided</p>
                      )}
                    </div>
                    <button
                      onClick={() => deleteGuest(g.id, g.guest_name)}
                      className="opacity-0 group-hover:opacity-100 transition-opacity p-1.5 rounded-lg hover:bg-red-500/20 text-white/30 hover:text-red-400"
                      title="Remove guest"
                      data-testid={`delete-guest-${g.id}`}
                    >
                      <X className="w-4 h-4" />
                    </button>
                  </div>
                ))}
              </div>
            )}
          </div>

          {guests.filter(g => g.guest_email).length > 0 && photos.length > 0 && (
            <div className="bg-blue-500/10 border border-blue-500/20 rounded-xl p-3" data-testid="gallery-email-info">
              <p className="text-sm text-blue-300">
                Clicking "Send Gallery to Guests" will email a link to the photo gallery to all {guests.filter(g => g.guest_email).length} guest{guests.filter(g => g.guest_email).length !== 1 ? 's' : ''} who provided an email. They'll be able to view and download all event photos.
              </p>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
